import { PenTool, Target, LayoutGrid, FileText, AlertOctagon, LineChart } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * Search content and conversion service page.
 * Focused on content that ranks and is structured to convert,
 * not just publishing volume.
 */
export const contentConversionPage: MoneyPageContent = {
  id: "content-conversion",
  icon: PenTool,
  serviceType: "Search Content and Conversion",
  esPath: "/servicios/contenido-conversion",
  enPath: "/en/services/content-conversion",
  es: {
    metaTitle: "Contenido SEO que Convierte | Estrategia y Estructura",
    metaDescription:
      "Contenido orientado a búsqueda que también convierte: mapeo de intención a tipo de página, arquitectura de contenido y estructura on-page para negocio.",
    keywords: [
      "contenido seo",
      "contenido que convierte",
      "arquitectura de contenido",
      "intención de búsqueda",
      "conversión seo",
    ],
    h1: "Contenido para buscadores que también convierte en negocio",
    breadcrumbLabel: "Contenido y Conversión",
    heroSubtitle: "Publicar contenido no es lo mismo que generar resultados",
    heroDescription:
      "El contenido que solo persigue palabras clave sin considerar la intención real ni el recorrido del usuario rara vez convierte. Trabajo el mapeo de intención a tipo de página, la arquitectura de contenido frente al volumen, la estructura on-page orientada a conversión y los estándares editoriales que sostienen ambas cosas a la vez.",
    ctaPrimary: {
      label: "Solicitar diagnóstico estratégico",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Intención mapeada a tipo de página",
        text: "Qué formato de página responde mejor a cada tipo de búsqueda: informativa, comparativa, transaccional.",
      },
      {
        label: "Arquitectura frente a volumen",
        text: "Cómo se organiza el contenido para que se refuerce entre sí, en lugar de publicar por publicar.",
      },
      {
        label: "Estructura on-page para conversión",
        text: "Dónde y cómo se ubican los elementos que llevan a la acción dentro de una página que también debe posicionar.",
      },
      {
        label: "Medición de conversiones asistidas",
        text: "Cómo se sigue el aporte del contenido orgánico a conversiones que no ocurren en la primera visita.",
      },
    ],
    explanation: {
      title: "Qué es el contenido orientado a búsqueda y conversión (y qué no es)",
      definition:
        "Es el trabajo de diseñar contenido que responde a una intención de búsqueda específica y que, al mismo tiempo, está estructurado para avanzar al usuario hacia una acción de negocio. No es escribir artículos alrededor de palabras clave ni aumentar el volumen de publicación esperando que algo funcione: es decidir qué contenido crear, en qué formato, y cómo se conecta con el resto del sitio y con el objetivo comercial.",
      whyImportant: {
        title: "Por qué importa más que la cantidad de contenido",
        text: "Tráfico que no convierte no sostiene un negocio, sin importar cuántas visitas genere. El contenido bien estructurado atrae a la persona correcta en el momento correcto y la guía hacia una decisión, en lugar de dejarla en una página aislada.",
      },
      consequences: {
        title: "Qué pasa si se ignora",
        text: "Contenido que posiciona pero no convierte, canibalización entre artículos que compiten por la misma intención, catálogos de contenido difíciles de mantener, y decisiones editoriales basadas en volumen en lugar de en impacto de negocio.",
      },
    },
    problems: {
      title: "Problemas que trabajo con más frecuencia",
      subtitle: "Cada uno limita el retorno del contenido, aunque el tráfico se vea bien",
      items: [
        {
          icon: Target,
          title: "Desajuste entre intención y tipo de página",
          description:
            "Contenido informativo intentando vender directamente, o páginas de producto compitiendo por búsquedas educativas que necesitan otro formato.",
          impact: "Tráfico que llega, pero no encuentra lo que esperaba encontrar.",
        },
        {
          icon: LayoutGrid,
          title: "Volumen sin arquitectura",
          description:
            "Publicación constante de artículos sin una estructura de temas que se refuercen entre sí ni una jerarquía clara de páginas pilar y de apoyo.",
          impact: "Canibalización de palabras clave y autoridad temática diluida.",
        },
        {
          icon: FileText,
          title: "Estructura on-page pensada solo para SEO",
          description:
            "Páginas optimizadas para posicionar que no incluyen los elementos necesarios para guiar al usuario hacia una acción concreta.",
          impact: "Posiciones altas que no se traducen en leads, ventas o registros.",
        },
        {
          icon: AlertOctagon,
          title: "Estándares editoriales inconsistentes",
          description:
            "Contenido de calidad desigual entre distintos autores o proveedores, sin criterios claros de precisión, profundidad o tono.",
          impact: "Pérdida de confianza del usuario y señales de calidad más débiles para los buscadores.",
        },
        {
          icon: LineChart,
          title: "Medición incompleta del aporte del contenido",
          description:
            "Se mide solo la conversión directa en la misma sesión, ignorando el rol del contenido en recorridos de compra más largos.",
          impact: "Contenido que parece no funcionar cuando en realidad influye antes de la conversión final.",
        },
      ],
    },
    methodology: {
      title: "Cómo abordo el contenido orientado a búsqueda y conversión",
      subtitle: "Decisiones editoriales basadas en intención y en el recorrido del negocio",
      steps: [
        {
          number: "01",
          title: "Mapeo de intención a tipo de página",
          description:
            "Clasificación de las búsquedas relevantes según intención real y definición del formato de página más adecuado para cada una.",
        },
        {
          number: "02",
          title: "Arquitectura de contenido",
          description:
            "Diseño de la relación entre páginas pilar y de apoyo, priorizando cobertura temática coherente por encima del volumen de publicación.",
        },
        {
          number: "03",
          title: "Estructura on-page orientada a conversión",
          description:
            "Definición de dónde y cómo aparecen los elementos de conversión dentro de una página que también debe cumplir su función de posicionamiento.",
        },
        {
          number: "04",
          title: "Estándares editoriales y medición",
          description:
            "Criterios de calidad para la producción de contenido y definición de cómo se hará seguimiento de conversiones asistidas, no solo directas.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Mapeo de intención de búsqueda a tipo de página",
          "Diseño de arquitectura de contenido (pilares y páginas de apoyo)",
          "Definición de estructura on-page orientada a conversión",
          "Estándares editoriales y criterios de calidad de contenido",
          "Marco de medición para conversiones asistidas por contenido orgánico",
          "Priorización de qué contenido crear, actualizar o eliminar",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Redacción masiva de artículos como servicio de producción de volumen",
          "Diseño visual completo de plantillas o UI",
          "Gestión de campañas de pago o distribución en redes sociales",
          "Trabajo técnico de indexación o rastreo (ver SEO técnico)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Cuándo aplica este servicio",
        items: [
          "Sitios con tráfico orgánico que no se traduce en leads o ventas",
          "Equipos publicando contenido de forma constante sin estrategia clara",
          "Negocios con ciclos de decisión largos que necesitan contenido en varias etapas",
          "Catálogos de contenido grandes con posible canibalización entre páginas",
        ],
      },
      notFor: {
        title: "Cuándo no aplica",
        items: [
          "Quien busca únicamente volumen de artículos al menor costo posible",
          "Sitios sin capacidad de publicar o actualizar contenido de forma sostenida",
          "Negocios que buscan resultados de conversión inmediatos sin dar tiempo al posicionamiento",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿Este servicio incluye la redacción de los artículos?",
          answer:
            "El enfoque principal es la estrategia: qué contenido crear, en qué formato, cómo se estructura y cómo se mide. La producción de redacción se puede coordinar según el proyecto, pero no es un servicio de volumen de artículos.",
        },
        {
          question: "¿Cuántos artículos necesito publicar para ver resultados?",
          answer:
            "No hay un número fijo. Depende de la cobertura temática que falte, la competencia y la intención que se quiera cubrir. Publicar más contenido sin una arquitectura clara no suele mejorar los resultados.",
        },
        {
          question: "¿Cómo se mide si el contenido realmente está ayudando a vender?",
          answer:
            "Se define un marco de medición que considera conversiones asistidas, no solo la última interacción antes de la conversión. Esto permite ver el aporte real del contenido en recorridos de decisión más largos.",
        },
        {
          question: "¿El contenido optimizado para SEO puede convertir bien también?",
          answer:
            "Sí, si se diseña con ambos objetivos desde el inicio. El problema surge cuando se optimiza solo para posicionar o solo para convertir, sin pensar en cómo ambos objetivos conviven en la misma página.",
        },
        {
          question: "¿Qué pasa con el contenido antiguo que ya tenemos?",
          answer:
            "Se audita como parte del proceso: se identifica qué contenido conviene actualizar, consolidar o eliminar por canibalización o bajo desempeño, antes de decidir qué contenido nuevo crear.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "El contenido se apoya en otras áreas del sitio para funcionar",
      links: [
        {
          label: "SEO Técnico",
          description: "Rastreo, indexación y arquitectura como base para que el contenido posicione.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "SEO para ecommerce",
          description: "Consideraciones específicas de contenido en catálogos y páginas de producto.",
          to: "/servicios/ecommerce-seo",
        },
        {
          label: "Consultoría SEO",
          description: "Estrategia y acompañamiento continuo más allá del contenido.",
          to: "/consultor-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de contenido orientado a búsqueda y conversión: mapeo de intención a tipo de página, arquitectura de contenido frente a volumen, estructura on-page para conversión, estándares editoriales y medición de conversiones asistidas.",
    keyPoints: [
      "No es producción de volumen de artículos, es estrategia y estructura de contenido",
      "Mapea intención de búsqueda a formato de página específico",
      "Prioriza arquitectura de contenido sobre cantidad de publicaciones",
      "Incluye medición de conversiones asistidas, no solo directas",
    ],
  },
  en: {
    metaTitle: "Search Content That Converts | Strategy & Structure",
    metaDescription:
      "Search-led content that also converts: intent mapped to page types, content architecture over volume, and on-page structure built for business results.",
    keywords: [
      "seo content strategy",
      "content that converts",
      "content architecture",
      "search intent mapping",
      "conversion focused content",
    ],
    h1: "Content built for search that also converts into business",
    breadcrumbLabel: "Content & Conversion",
    heroSubtitle: "Publishing content isn't the same as generating results",
    heroDescription:
      "Content built around keywords alone, without factoring in real intent or the user's journey, rarely converts. I work on mapping intent to page type, content architecture versus raw volume, on-page structure built for conversion, and the editorial standards that hold both goals together.",
    ctaPrimary: {
      label: "Review My Content and Conversion Path",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Intent mapped to page type",
        text: "Which page format best answers each kind of search — informational, comparative, transactional.",
      },
      {
        label: "Architecture over volume",
        text: "How content is organized so pages reinforce each other, instead of publishing for the sake of publishing.",
      },
      {
        label: "On-page structure for conversion",
        text: "Where and how conversion elements sit inside a page that also has to rank.",
      },
      {
        label: "Measuring assisted conversions",
        text: "How organic content's contribution is tracked when conversions don't happen on the first visit.",
      },
    ],
    explanation: {
      title: "What search-led, conversion-focused content is (and isn't)",
      definition:
        "It's the work of designing content that answers a specific search intent while also being structured to move the user toward a business action. It's not writing articles around keywords or increasing publishing volume and hoping something works — it's deciding what content to create, in what format, and how it connects to the rest of the site and to a commercial goal.",
      whyImportant: {
        title: "Why it matters more than the amount of content",
        text: "Traffic that doesn't convert doesn't sustain a business, no matter how many visits it generates. Well-structured content attracts the right person at the right moment and guides them toward a decision, instead of leaving them on an isolated page.",
      },
      consequences: {
        title: "What happens if it's ignored",
        text: "Content that ranks but doesn't convert, keyword cannibalization between articles competing for the same intent, content catalogs that are hard to maintain, and editorial decisions driven by volume instead of business impact.",
      },
    },
    problems: {
      title: "Problems I work with most often",
      subtitle: "Each one caps the return on content, even when traffic looks healthy",
      items: [
        {
          icon: Target,
          title: "Mismatch between intent and page type",
          description:
            "Informational content trying to sell directly, or product pages competing for educational searches that need a different format.",
          impact: "Traffic arrives but doesn't find what it expected to find.",
        },
        {
          icon: LayoutGrid,
          title: "Volume without architecture",
          description:
            "Constant article publishing without a topic structure that reinforces itself, and no clear hierarchy of pillar and supporting pages.",
          impact: "Keyword cannibalization and diluted topical authority.",
        },
        {
          icon: FileText,
          title: "On-page structure built only for SEO",
          description:
            "Pages optimized to rank that don't include the elements needed to guide the user toward a concrete action.",
          impact: "Strong rankings that don't translate into leads, sales or sign-ups.",
        },
        {
          icon: AlertOctagon,
          title: "Inconsistent editorial standards",
          description:
            "Uneven content quality across writers or vendors, with no clear criteria for accuracy, depth or tone.",
          impact: "Eroded user trust and weaker quality signals for search engines.",
        },
        {
          icon: LineChart,
          title: "Incomplete measurement of content's contribution",
          description:
            "Only same-session direct conversions get measured, ignoring content's role across longer buying journeys.",
          impact: "Content that looks like it isn't working when it's actually influencing decisions upstream.",
        },
      ],
    },
    methodology: {
      title: "How I approach search-led content and conversion",
      subtitle: "Editorial decisions based on intent and the business journey",
      steps: [
        {
          number: "01",
          title: "Mapping intent to page type",
          description:
            "Classifying relevant searches by real intent and defining the most suitable page format for each one.",
        },
        {
          number: "02",
          title: "Content architecture",
          description:
            "Designing the relationship between pillar and supporting pages, prioritizing coherent topical coverage over publishing volume.",
        },
        {
          number: "03",
          title: "On-page structure built for conversion",
          description:
            "Defining where and how conversion elements appear within a page that still has to do its job of ranking.",
        },
        {
          number: "04",
          title: "Editorial standards and measurement",
          description:
            "Quality criteria for content production, plus a framework for tracking assisted conversions, not just direct ones.",
        },
      ],
      ctaText: "Review My Content and Conversion Path",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "Mapping search intent to page type",
          "Content architecture design (pillar and supporting pages)",
          "On-page structure definition built for conversion",
          "Editorial standards and content quality criteria",
          "A measurement framework for content-assisted conversions",
          "Prioritization of what content to create, update or remove",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Bulk article writing as a high-volume production service",
          "Full visual design of templates or UI",
          "Managing paid campaigns or social media distribution",
          "Technical indexing or crawling work (see technical SEO)",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "When this applies",
        items: [
          "Sites with organic traffic that isn't translating into leads or sales",
          "Teams publishing content consistently without a clear strategy",
          "Businesses with long decision cycles that need content across multiple stages",
          "Large content catalogs with possible cannibalization between pages",
        ],
      },
      notFor: {
        title: "When it doesn't apply",
        items: [
          "Anyone looking purely for the lowest-cost article volume",
          "Sites without the capacity to publish or update content consistently",
          "Businesses expecting immediate conversion results without allowing time for rankings to build",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Does this service include writing the articles?",
          answer:
            "The main focus is strategy: what content to create, in what format, how it's structured and how it's measured. Writing production can be coordinated depending on the project, but this isn't a bulk article service.",
        },
        {
          question: "How many articles do I need to publish to see results?",
          answer:
            "There's no fixed number. It depends on the topical coverage that's missing, the competition and the intent you need to address. Publishing more content without clear architecture usually doesn't improve results.",
        },
        {
          question: "How do you measure whether content is actually helping sales?",
          answer:
            "A measurement framework is defined that accounts for assisted conversions, not just the last interaction before a conversion. That makes it possible to see content's real contribution across longer decision journeys.",
        },
        {
          question: "Can content optimized for SEO also convert well?",
          answer:
            "Yes, if it's designed with both goals from the start. The problem shows up when content is optimized only to rank or only to convert, without considering how both goals coexist on the same page.",
        },
        {
          question: "What happens with the existing content we already have?",
          answer:
            "It gets audited as part of the process — identifying what should be updated, consolidated or removed due to cannibalization or underperformance, before deciding what new content to create.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "Content depends on other parts of the site to actually work",
      links: [
        {
          label: "Technical SEO",
          description: "Crawling, indexing and architecture as the foundation for content to rank.",
          to: "/en/services/technical-seo",
        },
        {
          label: "Ecommerce SEO",
          description: "Content considerations specific to catalogs and product pages.",
          to: "/en/services/ecommerce-seo",
        },
        {
          label: "SEO consulting",
          description: "Ongoing strategy and guidance beyond content alone.",
          to: "/en/seo-consultant",
        },
      ],
    },
    llmSummary:
      "Search-led, conversion-focused content service: mapping intent to page type, content architecture over volume, on-page structure for conversion, editorial standards and measurement of assisted conversions.",
    keyPoints: [
      "Not bulk article production — this is content strategy and structure",
      "Maps search intent to a specific page format",
      "Prioritizes content architecture over publishing volume",
      "Includes measuring assisted conversions, not just direct ones",
    ],
  },
};
